/**
 * Gate pass detail — read the document, and do the next thing to it.
 *
 * Every button here comes from getGatePassActions(), which is permission-filtered
 * and status-aware, so the screen never offers something the backend will reject.
 *
 * The lifecycle strip is built locally rather than with WorkflowPipeline: that
 * component tracks the CURRENT ROUTE across screens, not a document's status, so
 * it would light up nothing here.
 */
import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  Box, Grid, Alert, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody,
  Typography, LinearProgress, Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import InvButton from '../../components/InvButton';
import StatusChip from '../../components/StatusChip';
import ConfirmDialog from '../../components/ConfirmDialog';
import LetterheadMenu, { useLetterheadMenu } from '../../components/LetterheadMenu';
import GateOutDialog from '../../components/stock/GateOutDialog';
import {
  getGatePass,
  submitGatePass,
  approveGatePass,
  rejectGatePass,
  cancelGatePass,
  gateOutGatePass,
  gateInGatePass,
  closeNotReturned,
} from '../../api/gatePass';
import { getStores } from '../../api/stockLedger';
import { toInventoryList } from '../../api/inventory-request';
import {
  GATE_PASS_PURPOSE_LABELS,
  GATE_PASS_PARTY_TYPE_LABELS,
  GATE_PASS_DIRECTION_LABELS,
} from '../../constants/gatePass';
import { storeDisplayName } from '../../utils/storeEntity';
import { formatDate, formatDateTime } from '../../utils';
import useInvRole from '../../hooks/useInvRole';
import {
  getGatePassActions,
  pendingStageOf,
  isReturnable,
  isOverdue,
  daysOut,
  daysOverdue,
  returnProgress,
  pendingReturnQty,
  GATE_PASS_ACTIONS as A,
} from '../../utils/gatePassEntity';
import { printGatePassInNewTab, downloadGatePassPdfFromData } from '../../utils/gatePassPdf';
import { BrandingContext } from '../../../../BrandingProvider';
import { IPD_COLORS } from '../../theme';

/** The lifecycle strip. Returnable passes have one more step than the rest. */
function lifecycleSteps(gatePass) {
  const status = String(gatePass?.status || '').toUpperCase();
  const inward = String(gatePass?.direction || '').toUpperCase() === 'INWARD';
  if (inward) {
    return [
      { key: 'DRAFT', label: 'Draft', done: true },
      { key: 'GATE_IN', label: 'Gate In', done: ['GATE_IN', 'CLOSED'].includes(status) },
      { key: 'CLOSED', label: 'Closed', done: status === 'CLOSED' },
    ];
  }
  const approved = ['APPROVED', 'GATE_OUT', 'PARTIALLY_RETURNED', 'RETURNED', 'CLOSED', 'CLOSED_NOT_RETURNED'].includes(status);
  const out = ['GATE_OUT', 'PARTIALLY_RETURNED', 'RETURNED', 'CLOSED', 'CLOSED_NOT_RETURNED'].includes(status);
  const steps = [
    { key: 'DRAFT', label: 'Draft', done: true },
    { key: 'APPROVED', label: 'Approved', done: approved },
    { key: 'GATE_OUT', label: 'Gate Out', done: out },
  ];
  if (isReturnable(gatePass)) {
    steps.push({
      key: 'RETURNED',
      label: 'Returned',
      done: ['RETURNED', 'CLOSED', 'CLOSED_NOT_RETURNED'].includes(status),
    });
  } else {
    steps.push({ key: 'CLOSED', label: 'Closed', done: status === 'CLOSED' });
  }
  return steps;
}

function Field({ label, value }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary, mb: 0.25 }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, color: IPD_COLORS.textPrimary }}>{value || '—'}</Typography>
    </Box>
  );
}

export default function GatePassDetail() {
  const history = useHistory();
  const { id } = useParams();
  const { url } = useRouteMatch();
  const { can } = useInvRole();
  const letterheadMenu = useLetterheadMenu();
  const brandingData = useContext(BrandingContext);
  const facilityLogo = (() => {
    const img = brandingData?.find(b => b.name === 'collateral_image')?.content;
    return img ? `data:image/png;base64,${img}` : null;
  })();

  const base = url.replace(/\/[^/]+$/, '');
  const listUrl = url.replace(/\/gate-pass\/[^/]+$/, '/gate-passes');

  const [gatePass, setGatePass] = useState(null);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [acting, setActing] = useState(false);

  const [confirmAction, setConfirmAction] = useState(null);
  const [reason, setReason] = useState('');
  const [gateMode, setGateMode] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // API: GET /inventory/gate-pass/{id}/
      setGatePass(await getGatePass(id));
    } catch (e) {
      setError(e.message || 'Failed to load gate pass');
      setGatePass(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getStores()
      .then(d => setStores(toInventoryList(d)))
      .catch(() => {});
  }, []);

  const storeName = (storeId) => {
    if (!storeId) return '—';
    const s = stores.find(r => (r.store_id || r.id) === storeId);
    return s ? storeDisplayName(s) : storeId;
  };

  async function runConfirm() {
    if (!confirmAction) return;
    setActing(true);
    try {
      const stage = pendingStageOf(gatePass);
      if (confirmAction === 'submit') {
        await submitGatePass(id);
        toast.success('Gate pass sent for approval');
      } else if (confirmAction === 'approve') {
        await approveGatePass(id, { stage });
        toast.success('Gate pass approved');
      } else if (confirmAction === 'reject') {
        await rejectGatePass(id, { stage, rejection_reason: reason.trim() });
        toast.success('Gate pass rejected');
      } else if (confirmAction === 'cancel') {
        await cancelGatePass(id);
        toast.success('Gate pass cancelled');
      } else if (confirmAction === 'close-not-returned') {
        await closeNotReturned(id, { reason: reason.trim() });
        toast.success('Closed as not returned');
      }
      setConfirmAction(null);
      setReason('');
      load();
    } catch (e) {
      toast.error(e.message || 'Action failed');
    } finally {
      setActing(false);
    }
  }

  async function runGate(form) {
    setActing(true);
    try {
      // The combined button is a Phase-1 convenience for the one person who holds
      // both permissions: approve first, then the physical exit, in that order.
      if (gateMode === 'approve-and-gate-out') {
        await approveGatePass(id, { stage: pendingStageOf(gatePass) });
      }
      if (gateMode === 'gate-in') {
        await gateInGatePass(id, form);
        toast.success('Gate in recorded');
      } else {
        await gateOutGatePass(id, form);
        toast.success('Gate out recorded');
      }
      setGateMode(null);
      load();
    } catch (e) {
      toast.error(e.message || 'Action failed');
    } finally {
      setActing(false);
    }
  }

  /**
   * The challan is built from the document already in state — GET /gate-pass/{id}/
   * returns the header, the resolved item names and the approval trail in one
   * payload, so there is nothing left to fetch.
   */
  function pdfData(letterhead) {
    return { gatePass, storeName: storeName(gatePass.store_id), facilityLogo, letterhead };
  }

  function handlePrint(letterhead) {
    try {
      printGatePassInNewTab(pdfData(letterhead));
    } catch (e) {
      toast.error(e.message || 'Failed to generate the gate pass');
    }
  }

  function handleDownload(letterhead) {
    try {
      downloadGatePassPdfFromData(pdfData(letterhead));
    } catch (e) {
      toast.error(e.message || 'Failed to generate the gate pass');
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress sx={{ color: IPD_COLORS.primary }} />
      </Box>
    );
  }

  if (error || !gatePass) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error || 'Gate pass not found'}</Alert>
        <InvButton variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => history.push(listUrl)}>
          Back
        </InvButton>
      </Box>
    );
  }

  const actions = getGatePassActions(gatePass, can);
  const returnable = isReturnable(gatePass);
  const lines = gatePass.lines || [];
  const progress = returnProgress(lines);
  const overdue = isOverdue(gatePass);
  const steps = lifecycleSteps(gatePass);

  const confirmCopy = {
    submit: { title: 'Send for Approval', description: 'Send this gate pass for approval?', label: 'Send' },
    approve: {
      title: 'Approve Gate Pass',
      description: 'Approve this gate pass? The material can then leave the premises.',
      label: 'Approve',
    },
    reject: { title: 'Reject Gate Pass', description: 'Reject this gate pass? The material stays inside.', label: 'Reject' },
    cancel: { title: 'Cancel Gate Pass', description: 'Cancel this gate pass? This cannot be undone.', label: 'Cancel gate pass' },
    'close-not-returned': {
      title: 'Close as Not Returned',
      description: 'This pass will be closed as not returned and stop appearing as overdue. Nothing is written off your stock — correct the books with a stock adjustment if they are wrong.',
      label: 'Close as not returned',
    },
  }[confirmAction] || {};

  return (
    <Box>
      <PageHeader
        breadcrumb={['Inventory Management', 'Gate Pass']}
        title={gatePass.gate_pass_number}
        description={`${GATE_PASS_DIRECTION_LABELS[gatePass.direction] || gatePass.direction} · ${returnable ? 'Returnable' : 'Non-returnable'} · ${GATE_PASS_PURPOSE_LABELS[gatePass.purpose] || gatePass.purpose}`}
        badge={<StatusChip status={gatePass.status} domain="gatePass" />}
        actions={(
          <>
            <InvButton variant="outlined" size="small" startIcon={<ArrowBackIcon />} onClick={() => history.push(listUrl)}>
              Back
            </InvButton>
            {actions.has(A.PRINT) && (
              <>
                <InvButton
                  variant="outlined"
                  size="small"
                  startIcon={<PrintIcon />}
                  onClick={e => letterheadMenu.open(e, handlePrint)}
                >
                  Print
                </InvButton>
                <InvButton
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={e => letterheadMenu.open(e, handleDownload)}
                >
                  Download
                </InvButton>
                <LetterheadMenu menu={letterheadMenu} label="Gate Pass" />
              </>
            )}
            {actions.has(A.EDIT) && (
              <InvButton variant="outlined" size="small" onClick={() => history.push(`${base}/${id}/edit`)}>
                Edit
              </InvButton>
            )}
            {actions.has(A.SUBMIT) && (
              <InvButton size="small" onClick={() => setConfirmAction('submit')}>Send for approval</InvButton>
            )}
            {actions.has(A.REJECT) && (
              <InvButton variant="outlined" size="small" onClick={() => { setReason(''); setConfirmAction('reject'); }}>
                Reject
              </InvButton>
            )}
            {actions.has(A.APPROVE) && !actions.has(A.APPROVE_AND_GATE_OUT) && (
              <InvButton size="small" onClick={() => setConfirmAction('approve')}>Approve</InvButton>
            )}
            {actions.has(A.APPROVE_AND_GATE_OUT) && (
              <>
                <InvButton variant="outlined" size="small" onClick={() => setConfirmAction('approve')}>
                  Approve only
                </InvButton>
                <InvButton size="small" onClick={() => setGateMode('approve-and-gate-out')}>
                  Approve &amp; gate out
                </InvButton>
              </>
            )}
            {actions.has(A.GATE_OUT) && (
              <InvButton size="small" onClick={() => setGateMode('gate-out')}>Record gate out</InvButton>
            )}
            {actions.has(A.GATE_IN) && (
              <InvButton size="small" onClick={() => setGateMode('gate-in')}>Record gate in</InvButton>
            )}
            {actions.has(A.RECORD_RETURN) && (
              <InvButton size="small" onClick={() => history.push(`${base}/${id}/return`)}>
                Record return
              </InvButton>
            )}
            {actions.has(A.CLOSE_NOT_RETURNED) && (
              <InvButton variant="outlined" size="small" onClick={() => { setReason(''); setConfirmAction('close-not-returned'); }}>
                Not returned
              </InvButton>
            )}
            {actions.has(A.CANCEL) && (
              <InvButton variant="outlined" size="small" onClick={() => setConfirmAction('cancel')}>
                Cancel
              </InvButton>
            )}
          </>
        )}
      />

      {overdue && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Expected back on {formatDate(gatePass.expected_return_date)} — {daysOverdue(gatePass)} days overdue.
        </Alert>
      )}

      <SectionCard title="Progress">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {steps.map((step, i) => (
            <React.Fragment key={step.key}>
              {i > 0 && <Box sx={{ width: 28, height: 1, backgroundColor: IPD_COLORS.border }} />}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {step.done
                  ? <CheckCircleIcon sx={{ fontSize: 18, color: IPD_COLORS.success.text }} />
                  : <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: IPD_COLORS.textSecondary }} />}
                <Typography sx={{
                  fontSize: 13,
                  fontWeight: step.done ? 600 : 400,
                  color: step.done ? IPD_COLORS.textPrimary : IPD_COLORS.textSecondary,
                }}>
                  {step.label}
                </Typography>
              </Box>
            </React.Fragment>
          ))}
        </Box>

        {returnable && gatePass.gate_out_at && (
          <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, mt: 1.5 }}>
            Out for {daysOut(gatePass)} day{daysOut(gatePass) === 1 ? '' : 's'}
            {gatePass.expected_return_date ? ` · expected back ${formatDate(gatePass.expected_return_date)}` : ''}
            {` · ${progress.returned} of ${progress.total} returned`}
          </Typography>
        )}
      </SectionCard>

      <SectionCard title="Details" sx={{ mt: 2.5 }}>
        <Grid container spacing={2.5}>
          <Grid item xs={6} sm={4} md={3}><Field label="Date & time" value={formatDateTime(gatePass.gate_pass_datetime)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><Field label="Store" value={storeName(gatePass.store_id)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><Field label="Party type" value={GATE_PASS_PARTY_TYPE_LABELS[gatePass.party_type] || gatePass.party_type} /></Grid>
          <Grid item xs={6} sm={4} md={3}><Field label="Party" value={gatePass.party_name} /></Grid>
          <Grid item xs={6} sm={4} md={3}><Field label="Contact" value={gatePass.party_contact} /></Grid>
          <Grid item xs={6} sm={4} md={3}><Field label="Vehicle no." value={gatePass.vehicle_no} /></Grid>
          <Grid item xs={6} sm={4} md={3}><Field label="Carrier" value={gatePass.carrier_name} /></Grid>
          <Grid item xs={6} sm={4} md={3}><Field label="Carrier contact" value={gatePass.carrier_contact} /></Grid>
          {returnable && (
            <Grid item xs={6} sm={4} md={3}><Field label="Expected return" value={formatDate(gatePass.expected_return_date)} /></Grid>
          )}
          {gatePass.gate_out_at && (
            <Grid item xs={6} sm={4} md={3}><Field label="Gate out at" value={formatDateTime(gatePass.gate_out_at)} /></Grid>
          )}
          {gatePass.credit_note_number && (
            <>
              <Grid item xs={6} sm={4} md={3}><Field label="Credit note" value={gatePass.credit_note_number} /></Grid>
              <Grid item xs={6} sm={4} md={3}><Field label="CN date" value={formatDate(gatePass.credit_note_date)} /></Grid>
              <Grid item xs={6} sm={4} md={3}><Field label="CN amount" value={gatePass.credit_note_amount} /></Grid>
            </>
          )}
          {gatePass.purpose_remarks && (
            <Grid item xs={12}><Field label="Purpose detail" value={gatePass.purpose_remarks} /></Grid>
          )}
          {gatePass.remarks && (
            <Grid item xs={12}><Field label="Remarks" value={gatePass.remarks} /></Grid>
          )}
          {gatePass.rejection_reason && (
            <Grid item xs={12}>
              <Alert severity="warning">Rejected: {gatePass.rejection_reason}</Alert>
            </Grid>
          )}
        </Grid>
      </SectionCard>

      <SectionCard title="Items" sx={{ mt: 2.5 }}>
        {returnable && progress.total > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, mb: 0.5 }}>
              {progress.returned} of {progress.total} returned · {progress.pending} still outside
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress.pct}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: 'rgba(13,35,63,0.08)',
                '& .MuiLinearProgress-bar': { backgroundColor: IPD_COLORS.success.text },
              }}
            />
          </Box>
        )}

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: IPD_COLORS.textSecondary }}>#</TableCell>
                <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Item</TableCell>
                <TableCell sx={{ color: IPD_COLORS.textSecondary }}>
                  {returnable ? 'Batch no. / Serial no.' : 'Batch no.'}
                </TableCell>
                <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Qty</TableCell>
                {returnable && <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Returned</TableCell>}
                {returnable && <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Pending</TableCell>}
                <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Remarks</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((line, i) => (
                <TableRow key={line.id || i}>
                  <TableCell>{line.line_no ?? i + 1}</TableCell>
                  <TableCell>{line.item_name || line.item_description || '—'}</TableCell>
                  {/* A returnable line is a store line OR an off-master one, so it
                      carries a batch or a serial number — never both. */}
                  <TableCell>{line.batch_number || line.serial_number || '—'}</TableCell>
                  <TableCell>{line.quantity}</TableCell>
                  {returnable && <TableCell>{line.returned_quantity ?? 0}</TableCell>}
                  {returnable && <TableCell>{pendingReturnQty(line)}</TableCell>}
                  <TableCell>{line.remarks || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </SectionCard>

      {(gatePass.approvals || []).length > 0 && (
        <SectionCard title="Approval trail" sx={{ mt: 2.5 }}>
          {gatePass.approvals.map((a, i) => (
            <Box key={a.id || i}>
              {i > 0 && <Divider sx={{ my: 1.25, borderColor: IPD_COLORS.border }} />}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 13, color: IPD_COLORS.textPrimary }}>
                  <strong>{a.stage}</strong> · {a.action}
                  {a.actor_name ? ` · ${a.actor_name}` : ''}
                  {a.remarks ? ` — ${a.remarks}` : ''}
                </Typography>
                <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                  {dayjs(a.created_at).format('DD MMM YYYY, HH:mm')}
                </Typography>
              </Box>
            </Box>
          ))}
        </SectionCard>
      )}

      <ConfirmDialog
        open={Boolean(confirmAction)}
        title={confirmCopy.title}
        description={confirmCopy.description}
        destructive={confirmAction === 'cancel' || confirmAction === 'close-not-returned'}
        requireReason={confirmAction === 'reject' || confirmAction === 'close-not-returned'}
        reasonLabel={confirmAction === 'reject' ? 'Rejection reason' : 'Why is it not coming back?'}
        reasonValue={reason}
        onReasonChange={setReason}
        loading={acting}
        onConfirm={runConfirm}
        onCancel={() => { setConfirmAction(null); setReason(''); }}
        confirmLabel={confirmCopy.label}
      />

      <GateOutDialog
        open={Boolean(gateMode)}
        gatePass={gatePass}
        mode={gateMode === 'gate-in' ? 'gate-in' : gateMode}
        loading={acting}
        onConfirm={runGate}
        onCancel={() => setGateMode(null)}
      />
    </Box>
  );
}
